/**
 * Single mutable app state with pub/sub. Every preset mutation re-validates
 * via the shared validator; the UI shows errors inline and the preview only
 * reloads valid presets. No framework — KraftCAD spirit.
 */

import type { Preset } from "../../schema/types.ts";
import { validatePreset } from "../../schema/validator.ts";

export type Selection =
  | { kind: "master" }
  | { kind: "layer"; layerId: string }
  | { kind: "segment"; layerId: string; segIndex: number }
  | null;

export type ChangeKind = "preset" | "selection" | "transport";

export interface AppState {
  preset: Preset;
  selection: Selection;
  errors: string[];
  positionSec: number;
  playing: boolean;
}

type Listener = (state: AppState, what: ChangeKind) => void;

export class Store {
  readonly state: AppState;
  /** Audition mutes — affect preview/bounce only, never the saved preset. */
  readonly muted = new Set<string>();
  private listeners: Listener[] = [];

  constructor(initial: Preset) {
    this.state = {
      preset: initial,
      selection: null,
      errors: [],
      positionSec: 0,
      playing: false,
    };
    this.revalidate();
  }

  subscribe(fn: Listener): void {
    this.listeners.push(fn);
  }

  private emit(what: ChangeKind): void {
    for (const fn of this.listeners) fn(this.state, what);
  }

  get valid(): boolean {
    return this.state.errors.length === 0;
  }

  private revalidate(): void {
    const result = validatePreset(this.state.preset);
    this.state.errors = result.ok ? [] : result.errors;
  }

  /** Mutate the preset in place; revalidates and notifies. */
  updatePreset(mutate: (p: Preset) => void): void {
    mutate(this.state.preset);
    this.revalidate();
    this.emit("preset");
  }

  replacePreset(preset: Preset): void {
    this.state.preset = preset;
    this.state.selection = null;
    this.state.positionSec = 0;
    this.revalidate();
    this.emit("preset");
  }

  select(selection: Selection): void {
    this.state.selection = selection;
    this.emit("selection");
  }

  toggleMute(layerId: string): void {
    if (!this.muted.delete(layerId)) this.muted.add(layerId);
    this.emit("preset"); // preview must resync and the timeline redraw
  }

  setTransport(positionSec: number, playing: boolean): void {
    this.state.positionSec = positionSec;
    this.state.playing = playing;
    this.emit("transport");
  }
}
